import type { Feature, FeatureCollection } from 'geojson';

import type { MapAreaShapes } from '../api/types';

// Flächen der Karte: Landkreise (BKG VG250) und PLZ-Gebiete (OpenStreetMap) als öffentliche Kartendaten unter /karten/
// (einmal je Sitzung geladen). Farben kommen aus den Tokens des Designsystems (styles/global.css).

export type ShapeKind = 'kreise' | 'plz';

export const shapeAttribution: Record<ShapeKind, string> = {
  kreise: 'Landkreise © GeoBasis-DE / BKG',
  plz: 'PLZ-Gebiete © OpenStreetMap-Mitwirkende (ODbL)',
};

const loaded = new Map<string, Promise<unknown>>();

// cached: eine Datei unter /karten/ einmal je Sitzung laden (bei Fehler beim nächsten Mal erneut).
function cached<T>(path: string): Promise<T> {
  let p = loaded.get(path) as Promise<T> | undefined;
  if (!p) {
    p = fetch(`/karten/${path}`).then(async (r) => {
      if (!r.ok) throw new Error(`Kartendaten ${path} nicht verfügbar (${r.status})`);
      return (await r.json()) as T;
    });
    p.catch(() => loaded.delete(path));
    loaded.set(path, p);
  }
  return p;
}

// loadShapes: Landkreise als GeoJSON (Eigenschaften code und name).
export function loadShapes(kind: 'kreise'): Promise<Feature[]> {
  return cached<FeatureCollection>(`${kind}.json`).then((c) => c.features);
}

// PLZ-Flächen je Leitbereich (erste zwei Ziffern), damit nie alle 8.000 PLZ auf einmal geladen werden.
export type PlzIndex = Record<string, [number, number, number, number]>;

export function loadPlzIndex(): Promise<PlzIndex> {
  return cached<PlzIndex>('plz/index.json');
}

export function loadPlz(prefixes: string[]): Promise<Feature[]> {
  return Promise.all(prefixes.map((p) => cached<FeatureCollection>(`plz/${p}.json`).then((c) => c.features))).then((parts) => parts.flat());
}

// prefixesIn: Leitbereiche, die den Ausschnitt berühren.
export function prefixesIn(index: PlzIndex, v: { south: number; west: number; north: number; east: number }): string[] {
  return Object.entries(index)
    .filter(([, [s, w, n, e]]) => s <= v.north && n >= v.south && w <= v.east && e >= v.west)
    .map(([p]) => p)
    .sort();
}

export const shapeCode = (f: Feature): string => String(f.properties?.code ?? '');
export const shapeName = (f: Feature): string => String(f.properties?.name ?? f.properties?.code ?? '');

// cssVar: Wert eines CSS-Tokens (Leaflet braucht Farben als Zeichenkette).
export function cssVar(name: string, fallback = '#888888'): string {
  if (typeof document === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

export type AreaShape = MapAreaShapes['areas'][number];

// AreaLookup: welches Gebiet einen Landkreis bzw. eine PLZ umfasst (Bundesländer über die ersten zwei Ziffern).
export interface AreaLookup {
  byCounty: Map<string, AreaShape>;
  byPostalCode: Map<string, AreaShape>;
  byState: Map<string, AreaShape>;
  gaps: Map<string, number>;
  maxGap: number;
}

export function areaLookup(shapes?: MapAreaShapes): AreaLookup {
  const out: AreaLookup = { byCounty: new Map(), byPostalCode: new Map(), byState: new Map(), gaps: new Map(), maxGap: 0 };
  for (const a of shapes?.areas ?? []) {
    for (const c of a.county_codes) out.byCounty.set(c, a);
    for (const p of a.postal_codes) out.byPostalCode.set(p, a);
    for (const s of a.state_codes) out.byState.set(s, a);
  }
  for (const g of shapes?.gaps ?? []) {
    out.gaps.set(g.county_code, g.leads);
    out.maxGap = Math.max(out.maxGap, g.leads);
  }
  return out;
}

export function countyArea(l: AreaLookup, code: string): AreaShape | undefined {
  return l.byCounty.get(code) ?? l.byState.get(code.slice(0, 2));
}

// featureBounds: Ausschnitt [[Süd, West], [Nord, Ost]] um alle Flächen (null ohne Koordinaten).
export function featureBounds(features: Feature[]): [[number, number], [number, number]] | null {
  let south = 90;
  let west = 180;
  let north = -90;
  let east = -180;
  const visit = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number' && typeof c[1] === 'number') {
      const [lng, lat] = c as [number, number];
      south = Math.min(south, lat);
      north = Math.max(north, lat);
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      return;
    }
    for (const x of c) visit(x);
  };
  for (const f of features) if (f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates);
  return south <= north ? [[south, west], [north, east]] : null;
}
